"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.EntityProgramme = void 0;
const tslib_1 = require("tslib");
const iotv_v3_types_1 = require("@iotv/iotv-v3-types");
const uuid_1 = require("uuid");
const ProgrammeTypes_1 = require("../../../types/ProgrammeTypes");
const lambda_1 = require("../../../util/lambda");
const UTCPeriods_1 = require("../../../util/time/UTCPeriods");
const ModelCommon_1 = require("../../ModelCommon");
const enumerableDecorator_1 = require("../enumerableDecorator");
const types_1 = require("./types");
const debug = lambda_1.DEBUG_OVERRIDE || (lambda_1.NOT_LAMBDA && false);
class EntityProgramme extends ModelCommon_1.ModelCommon {
    constructor({ id = (0, uuid_1.v4)(), supportedType, enabled, maxNumberOfEntitiesPerTimeslot, name, timeslots, dayMask, ...others }) {
        super('EntityProgramme', { id, ...others });
        this.timeslotsToDateRanges = ({ relativeDate }) => {
            const unorderedDateRanges = this.timeslots.reduce((a, c) => {
                a.push(...(0, UTCPeriods_1.utcPeriodHrMinToDateRanges)({ relativeDate, utcPeriodHrMins: c, dayMask: this.dayMask }).map((dr) => {
                    return { enabled: this.enabled, dateRange: dr };
                }));
                return a;
            }, []);
            let sortedDateRanges = unorderedDateRanges;
            if (unorderedDateRanges.length > 1) {
                sortedDateRanges = unorderedDateRanges.sort((a, b) => (a.dateRange.at(0) < b.dateRange.at(0) ? -1 : 1));
            }
            return sortedDateRanges;
        };
        this.maxNumberOfEntitiesPerTimeslot = maxNumberOfEntitiesPerTimeslot ?? null;
        this.name = name;
        this.supportedType = supportedType;
        this.enabled = enabled ?? true;
        if (dayMask) {
            if ((0, iotv_v3_types_1.isDayMaskDefinition)(dayMask)) {
                this.dayMask = dayMask;
            }
            else {
                throw new Error(`EntityProgramme received invalid dayMask`);
            }
        }
        else {
            this.dayMask = iotv_v3_types_1.dayMaskAllTrueTemplate;
        }
        if (timeslots) {
            if (timeslots.every(iotv_v3_types_1.isUTCPeriodMins)) {
                this.timeslots = timeslots ?? [];
            }
            else {
                throw new Error(`EntityProgramme received invalid timeslots`);
            }
        }
        else {
            this.timeslots = [];
        }
    }
    getUserAttributes() {
        return {
            ...super.getUserAttributes(),
            supportedType: {
                type: 'Enum',
                editable: false,
                required: true,
                enumKv: ProgrammeTypes_1.ProgrammeSupportedTypesEnum,
            },
            maxNumberOfEntitiesPerTimeslot: {
                type: 'Number',
                default: null,
                editable: false,
                label: `Max Entities Per Slot`,
                precision: 0,
                required: false,
                description: 'the maximum number of entities allowed for a slot.',
            },
        };
    }
    getTimeslotsLength() {
        return this.timeslots.length;
    }
    pushTimeslot(timeslot) {
        this.timeslots.push(timeslot);
        return this;
    }
    popTimeslot() {
        return this.timeslots.pop();
    }
    // TODO delete slot, and leave gap - this requires manipulation of edges which is not ready yet - this is now done at EntityScheduler level
    insertTimeslot(newTimeslot, insertBehavior) {
        (0, UTCPeriods_1.insertUTCPeriodHrMinsIntoArray)({ newTimeslot, timeslots: this.timeslots, insertBehavior });
        return this;
    }
    isValid() {
        const isClassInstance = (0, types_1.isEntityProgramme)(this);
        const isValidArr = (0, iotv_v3_types_1.isAscUTCPeriodHrsMinsArr)(this.timeslots);
        debug && console.log({ isClassInstance, isValidArr });
        if (!isValidArr) {
            debug && console.log(`Invalid timeslots`, this.timeslots);
        }
        const isValid = isClassInstance && isValidArr;
        if (!isValid) {
            console.log(`EntityProgramme isValid returning false with`, { isClassInstance, isValidArr });
        }
        return isValid;
    }
    clone() {
        const { supportedType, dayMask: originalDayMask, timeslots: originalTimeSlots, maxNumberOfEntitiesPerTimeslot, name: originalName } = this;
        const name = `${originalName} clone`;
        const dayMask = Object.assign({}, originalDayMask);
        const timeslots = originalTimeSlots.map((timeslot) => Object.assign({}, timeslot));
        const enabled = this.enabled;
        return new EntityProgramme({ id: (0, uuid_1.v4)(), enabled, supportedType, maxNumberOfEntitiesPerTimeslot, dayMask, name, timeslots });
    }
}
exports.EntityProgramme = EntityProgramme;
tslib_1.__decorate([
    (0, enumerableDecorator_1.enumerable)(false),
    tslib_1.__metadata("design:type", Function),
    tslib_1.__metadata("design:paramtypes", []),
    tslib_1.__metadata("design:returntype", void 0)
], EntityProgramme.prototype, "getTimeslotsLength", null);
tslib_1.__decorate([
    (0, enumerableDecorator_1.enumerable)(false),
    tslib_1.__metadata("design:type", Function),
    tslib_1.__metadata("design:paramtypes", [Object]),
    tslib_1.__metadata("design:returntype", void 0)
], EntityProgramme.prototype, "pushTimeslot", null);
tslib_1.__decorate([
    (0, enumerableDecorator_1.enumerable)(false),
    tslib_1.__metadata("design:type", Function),
    tslib_1.__metadata("design:paramtypes", []),
    tslib_1.__metadata("design:returntype", void 0)
], EntityProgramme.prototype, "popTimeslot", null);
tslib_1.__decorate([
    (0, enumerableDecorator_1.enumerable)(false),
    tslib_1.__metadata("design:type", Function),
    tslib_1.__metadata("design:paramtypes", [Object, String]),
    tslib_1.__metadata("design:returntype", void 0)
], EntityProgramme.prototype, "insertTimeslot", null);
tslib_1.__decorate([
    (0, enumerableDecorator_1.enumerable)(false),
    tslib_1.__metadata("design:type", Function),
    tslib_1.__metadata("design:paramtypes", []),
    tslib_1.__metadata("design:returntype", void 0)
], EntityProgramme.prototype, "isValid", null);
tslib_1.__decorate([
    (0, enumerableDecorator_1.enumerable)(false),
    tslib_1.__metadata("design:type", Object)
], EntityProgramme.prototype, "timeslotsToDateRanges", void 0);
tslib_1.__decorate([
    (0, enumerableDecorator_1.enumerable)(false),
    tslib_1.__metadata("design:type", Function),
    tslib_1.__metadata("design:paramtypes", []),
    tslib_1.__metadata("design:returntype", void 0)
], EntityProgramme.prototype, "clone", null);
